const RESERVED_AFTER_COMMUNITY_SLUG = new Set(['post', 'settings', 'modtools', 'search', 'all', 'join']);

/**
 * Town-square post URLs must be `/community/{slug}/post/{postSlug}` (optional `/{locale}` prefix).
 * Rewrites legacy `/community/{slug}/{postSlug}` that omitted `/post/`.
 */
export function normalizeTownSquareCommunityPostPath(path: string): string {
  let localePrefix = '';
  let p = path;
  const localeMatch = p.match(/^(\/[a-z]{2})(?=\/community\/)/);
  if (localeMatch?.[1]) {
    localePrefix = localeMatch[1];
    p = p.slice(localePrefix.length);
  }

  if (/\/community\/[^/]+\/post\/[^/]+/.test(p)) {
    return `${localePrefix}${p}`;
  }

  const m = p.match(/^\/community\/([^/]+)\/([^/]+)$/);
  if (!m?.[1] || !m[2]) return path;
  const slug = m[1];
  const second = m[2];
  if (RESERVED_AFTER_COMMUNITY_SLUG.has(second)) return path;
  return `${localePrefix}/community/${slug}/post/${second}`;
}

/**
 * Relative town-square path to a community post comment. Uses `?c=` so SSR
 * can pre-load the ancestor thread before paint.
 */
export function buildCommunityPostCommentPath(
  communitySlug: string,
  postSlug: string,
  commentId: string | number
): string {
  const id = String(commentId);
  return `/community/${communitySlug}/post/${postSlug}?c=${encodeURIComponent(id)}`;
}

/**
 * Relative town-square path to an artwork comment. Uses `?c=` so SSR
 * can pre-load the target comment before paint.
 */
export function buildImageCommentPath(profileSlug: string, imageSlug: string, commentId: string | number): string {
  const id = String(commentId);
  return `/${profileSlug}/image/${imageSlug}?c=${encodeURIComponent(id)}`;
}

/**
 * The town-square path a notification points at, or null when it has no
 * specific destination.
 *
 * Relative on purpose: town-square links to it as-is, and the notification email
 * joins it to an absolute origin. Reads the row it is handed — metadata slugs
 * plus `targetId` when `targetType` is `community_comment` — so it stays pure
 * and costs no query.
 *
 * Scope is deliberately the community post surface, which is what the email's
 * lines are about. Image comments and DM threads have their own destinations and
 * are left to the caller: an email line for those falls back to the notifications
 * page rather than guessing at a URL.
 *
 * NOTE: `NotificationToastListener` in town-square resolves the same question for
 * in-app toasts and predates this. It also handles thread and image targets, so
 * it is not a straight substitution — worth converging on this once the toast
 * cases are covered here too.
 */
export function notificationCommunityPath(notification: {
  targetId?: string | number | null;
  targetType?: string | null;
  metadata?: Record<string, any> | null;
}): string | null {
  const metadata = notification.metadata;
  if (!metadata) return null;

  const read = (key: string): string | null => {
    const value = metadata[key];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  };

  const communitySlug = read('communitySlug');
  const postSlug = read('postSlug') ?? read('communityPostSlug');
  if (!communitySlug || !postSlug) return null;

  // A reply lands ON the comment, not merely on the post it lives under — the
  // whole point of the link is to save the reader hunting for it.
  //
  // Production `community_reply` writers store the comment id on `targetId`
  // (`targetType: 'community_comment'`) and do not write `commentId` /
  // `communityCommentId` into metadata. Those keys are the toast/email
  // aliases; `targetId` is the live column. Only treat `targetId` as a
  // comment when the row says it is one — a follow-post's `targetId` is the
  // post, and `?c=<postId>` would send the reader nowhere.
  const commentId = read('commentId') ?? read('communityCommentId') ?? commentIdFromTarget(notification);
  if (commentId) return buildCommunityPostCommentPath(communitySlug, postSlug, commentId);

  return `/community/${communitySlug}/post/${postSlug}`;
}

function commentIdFromTarget(notification: {
  targetId?: string | number | null;
  targetType?: string | null;
}): string | null {
  if ((notification.targetType ?? '').toLowerCase() !== 'community_comment') return null;
  if (notification.targetId == null) return null;

  const id = String(notification.targetId).trim();
  return id === '' ? null : id;
}
